import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { strategiesApi } from '@/services/api';

interface Strategy {
  id: string;
  name: string;
  type: string;
  isActive: boolean;
  isPaperTrading: boolean;
}

interface AutomationSessionFormProps {
  onSubmit: (sessionData: any) => Promise<void>;
  onCancel: () => void;
}

const AutomationSessionForm: React.FC<AutomationSessionFormProps> = ({
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPaperTrading: true,
    executionInterval: 15,
    maxDailyExecutions: 96,
    strategies: [] as string[],
    strategyExecutionMode: 'sequential',
    maxDailyVolume: 1000,
    maxPositionSizePct: 10,
    stopLossPct: 5
  });

  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadStrategies();
  }, []);

  const loadStrategies = async () => {
    try {
      setLoading(true);
      const response = await strategiesApi.getAll({ limit: 100 });
      const allStrategies = (response as any).data?.strategies || [];
      
      // Show all strategies (both active and inactive) for better UX
      // Users can select strategies and activate them as needed
      setStrategies(allStrategies);
      
      console.log(`[AUTOMATION_FORM] Loaded ${allStrategies.length} strategies:`, 
        allStrategies.map((s: Strategy) => `${s.name} (${s.isActive ? 'active' : 'inactive'})`));
    } catch (error) {
      console.error('Failed to load strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Session name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Session name must be at least 3 characters';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Session name must not exceed 100 characters';
    }

    if (formData.strategies.length === 0) {
      newErrors.strategies = 'At least one strategy must be selected';
    }

    // Numeric validations
    if (formData.executionInterval < 5 || formData.executionInterval > 1440) {
      newErrors.executionInterval = 'Execution interval must be between 5 and 1440 minutes';
    }

    if (formData.maxDailyExecutions < 1 || formData.maxDailyExecutions > 288) {
      newErrors.maxDailyExecutions = 'Max daily executions must be between 1 and 288';
    }

    if (formData.maxDailyVolume <= 0 || formData.maxDailyVolume > 100000) {
      newErrors.maxDailyVolume = 'Max daily volume must be between 0 and 100,000';
    }

    if (formData.maxPositionSizePct < 0.1 || formData.maxPositionSizePct > 100) {
      newErrors.maxPositionSizePct = 'Max position size must be between 0.1% and 100%';
    }

    if (formData.stopLossPct < 0.1 || formData.stopLossPct > 50) {
      newErrors.stopLossPct = 'Stop loss must be between 0.1% and 50%';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(formData);
    } catch (error: any) {
      console.error('Failed to create session:', error);
      setErrors({ submit: error.message || 'Failed to create automation session' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleStrategyToggle = (strategyId: string) => {
    setFormData(prev => ({
      ...prev,
      strategies: prev.strategies.includes(strategyId)
        ? prev.strategies.filter(id => id !== strategyId)
        : [...prev.strategies, strategyId]
    }));
  };

  const calculateMaxExecutions = (intervalMinutes: number) => {
    return Math.floor((24 * 60) / intervalMinutes);
  };

  const formatEstimatedCost = (volume: number, executions: number) => {
    // Rough estimate: assuming 0.1% trading fee
    const estimatedTrades = executions * formData.strategies.length * 0.3; // 30% execution rate assumption
    const estimatedCost = (volume * estimatedTrades * 0.001);
    return estimatedCost.toFixed(2);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-900">
            Create Automated Trading Session
          </h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* General Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={`block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="My Trading Bot"
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trading Mode
              </label>
              <select
                value={formData.isPaperTrading ? 'paper' : 'live'}
                onChange={(e) => setFormData(prev => ({ 
                  ...prev, 
                  isPaperTrading: e.target.value === 'paper'
                }))}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="paper">Paper Trading (Recommended)</option>
                <option value="live">Live Trading</option>
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {formData.isPaperTrading 
                  ? 'Simulate trades without real money' 
                  : '⚠️ Real money trading - use with caution'}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="Optional description of this automation session..."
            />
          </div>

          {/* Strategy Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Strategies to Execute *
            </label>
            
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading strategies...</div>
            ) : strategies.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No strategies found. Please create strategies first.
              </div>
            ) : (
              <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto">
                {strategies.map((strategy) => (
                  <div key={strategy.id} className="flex items-center py-2">
                    <input
                      type="checkbox"
                      id={`strategy-${strategy.id}`}
                      checked={formData.strategies.includes(strategy.id)}
                      onChange={() => handleStrategyToggle(strategy.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor={`strategy-${strategy.id}`}
                      className="ml-3 flex-1 cursor-pointer"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">
                          {strategy.name}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          strategy.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {strategy.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">
                        {strategy.type} • {strategy.isPaperTrading ? 'Paper' : 'Live'}
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            )}
            
            {errors.strategies && <p className="mt-1 text-sm text-red-600">{errors.strategies}</p>}
            
            <div className="mt-2 space-y-1">
              <div className="text-xs text-gray-500">
                Selected: {formData.strategies.length} strategies
              </div>
              {strategies.some(s => !s.isActive) && (
                <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">
                  💡 Tip: Inactive strategies can be selected but should be activated in the Strategies page for optimal performance.
                </div>
              )}
            </div>
          </div>

          {/* Execution Configuration */}
          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Execution Configuration</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Execution Interval (minutes)
                </label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={formData.executionInterval}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    executionInterval: parseInt(e.target.value) || 15
                  }))}
                  className={`block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.executionInterval ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.executionInterval && <p className="mt-1 text-sm text-red-600">{errors.executionInterval}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  How often to execute strategies (5-1440 minutes)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Daily Executions
                </label>
                <input
                  type="number"
                  min="1"
                  max="288"
                  value={formData.maxDailyExecutions}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    maxDailyExecutions: parseInt(e.target.value) || 96
                  }))}
                  className={`block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.maxDailyExecutions ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.maxDailyExecutions && <p className="mt-1 text-sm text-red-600">{errors.maxDailyExecutions}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  Max possible: {calculateMaxExecutions(formData.executionInterval)} per day
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Strategy Execution Mode
                </label>
                <select
                  value={formData.strategyExecutionMode}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    strategyExecutionMode: e.target.value
                  }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="sequential">Sequential</option>
                  <option value="parallel">Parallel</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  How to execute multiple strategies
                </p>
              </div>
            </div>
          </div>

          {/* Risk Management */}
          <div className="border-t pt-6">
            <h4 className="text-md font-medium text-gray-900 mb-4">Risk Management</h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Daily Volume ($)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100000"
                  step="0.01"
                  value={formData.maxDailyVolume}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    maxDailyVolume: parseFloat(e.target.value) || 1000
                  }))}
                  className={`block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.maxDailyVolume ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.maxDailyVolume && <p className="mt-1 text-sm text-red-600">{errors.maxDailyVolume}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  Maximum trading volume per day
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Position Size (%)
                </label>
                <input
                  type="number"
                  min="0.1"
                  max="100"
                  step="0.1"
                  value={formData.maxPositionSizePct}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    maxPositionSizePct: parseFloat(e.target.value) || 10
                  }))}
                  className={`block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.maxPositionSizePct ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.maxPositionSizePct && <p className="mt-1 text-sm text-red-600">{errors.maxPositionSizePct}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  Max % of portfolio per position
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stop Loss (%)
                </label>
                <input
                  type="number"
                  min="0.1"
                  max="50"
                  step="0.1"
                  value={formData.stopLossPct}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    stopLossPct: parseFloat(e.target.value) || 5
                  }))}
                  className={`block w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${
                    errors.stopLossPct ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {errors.stopLossPct && <p className="mt-1 text-sm text-red-600">{errors.stopLossPct}</p>}
                <p className="mt-1 text-xs text-gray-500">
                  Automatic stop loss threshold
                </p>
              </div>
            </div>
          </div>

          {/* Estimated Costs */}
          {formData.maxDailyVolume > 0 && formData.maxDailyExecutions > 0 && (
            <div className="bg-blue-50 p-4 rounded-md">
              <h5 className="text-sm font-medium text-blue-900 mb-2">Estimated Daily Costs</h5>
              <div className="text-xs text-blue-700 space-y-1">
                <div>Trading Volume: ${formData.maxDailyVolume}</div>
                <div>Max Executions: {formData.maxDailyExecutions}</div>
                <div>Estimated Trading Fees: ${formatEstimatedCost(formData.maxDailyVolume, formData.maxDailyExecutions)}</div>
                <div className="text-blue-600">
                  ⚠️ These are estimates. Actual costs may vary based on market conditions and strategy performance.
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 p-3 rounded-md">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || loading || strategies.length === 0}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Creating...' : 'Create Session'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutomationSessionForm; 